import { resolveId } from '@medplum/core'
import type {
  DocumentReference,
  HealthcareService,
  Reference,
} from '@medplum/fhirtypes'
import { getCodingsFromCodeableConcept } from '@seen/core/codeableConcept'
import { HEALTH_GORILLA_OBSERVATION_GROUP_PROFILE } from '@seen/core/constants'
import { formatDate } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import { invariant } from '@seen/core/invariant'
import * as appointments from '@seen/core/models/appointments/index'
import { ExtendedDiagnosticReport } from '@seen/core/models/diagnosticReport'
import type { ExtendedDocumentReference } from '@seen/core/models/document-references/documentReferences'
import * as healthcareServices from '@seen/core/models/healthcareService/index'
import { ExtendedObservation } from '@seen/core/models/observation'
import { OrderCategory } from '@seen/core/models/orders/constants'
import type { Order } from '@seen/core/models/orders/Order'
import { Participant } from '@seen/core/models/patients/participant'
import { ReviewLabResultsTask } from '@seen/core/models/tasks/standard/reviewLabResultsTask'
import type { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { VisitNote } from '@seen/core/models/visitNote'
import { getReferenceString } from '@seen/core/references'
import { isOfType } from '@seen/core/utils'
import {
  Badge,
  Button,
  cn,
  DotSeparator,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@seen/ui'
import {
  IconBuilding,
  IconCalendarPlus,
  IconEdit,
  IconExternalLink,
  IconFile,
  IconInfoCircle,
  IconLink,
  IconNotes,
  IconPlus,
  IconSparkles,
  IconUnlink,
  IconX,
} from '@tabler/icons-react'
import { compact, partition, sortBy, uniqBy } from 'lodash-es'
import React, {
  type JSX,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Activity } from '#src/components/activity/Activity.tsx'
import { AppointmentBadge } from '#src/components/AppointmentBadge.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { Checkbox } from '#src/components/core/Checkbox.tsx'
import { ExternalTextLink } from '#src/components/core/ExternalTextLink.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { HoverCard } from '#src/components/core/hover-card/HoverCard.tsx'
import { HoverCardContent } from '#src/components/core/hover-card/HoverCardContent.tsx'
import { HoverCardTrigger } from '#src/components/core/hover-card/HoverCardTrigger.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { PractitionerDisplay } from '#src/components/display/PractitionerDisplay.tsx'
import { DocumentReferenceSheet } from '#src/components/document-reference/DocumentReferenceSheet.tsx'
import { ParticipantNameplate } from '#src/components/nameplates/ParticipantNameplate.tsx'
import { ObservationHistoryChart } from '#src/components/observation/ObservationHistoryChart.tsx'
import { LabSubmissionErrorDisplay } from '#src/components/order/LabSubmissionErrorDisplay.tsx'
import { OrderLabelBadge } from '#src/components/order/OrderLabelBadge.tsx'
import { OrderLabelCombobox } from '#src/components/order/OrderLabelCombobox.tsx'
import { orderPriorityTooltipLabel } from '#src/components/order/OrderPriorityCombobox.tsx'
import { OrderSignature } from '#src/components/order/OrderSignature.tsx'
import { OrderTasks } from '#src/components/order/OrderTasks.tsx'
import { RelatedOrdersList } from '#src/components/order/RelatedOrdersList.tsx'
import { useOrderDocumentReferences } from '#src/components/order/useOrderDocumentReferences.ts'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import {
  createTaskFilter,
  useMarkRelatedInboxItemsAsDone,
} from '#src/hooks/inbox/index.ts'
import { useAstranaAuthorizationSearch } from '#src/hooks/useAstranaAuthorizationSearch.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useDocumentReviewStatus } from '#src/hooks/useDocumentReviewStatus.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useNavigateFromClickEvent } from '#src/hooks/useNavigateFromClickEvent.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useQuery } from '#src/hooks/useQuery.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useVisitNoteHref } from '#src/hooks/useVisitNoteHref.ts'
import { OrderActivity } from '#src/pages/task/OrderActivity.tsx'
import {
  buildCalendarQueryParam,
  ViewHorizon,
  ViewType,
} from '#src/stores/calendar.ts'
import { toast } from '#src/utils/toast.ts'

interface InternalOrderDisplayProps {
  order: Order
  isTwoColumn?: boolean
  showSuggestedOrderDocuments?: boolean
  onEditOrder?: () => void
}

export function OrderDisplay({
  order,
  isTwoColumn = false,
  showSuggestedOrderDocuments = true,
  onEditOrder,
}: InternalOrderDisplayProps): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const orderDetails = order.orderDetailsText
  const { explicit: documentReferences, suggested: suggestedDocuments } =
    useOrderDocumentReferences(order)
  const taskedForReview = useDocumentReviewStatus(documentReferences)
  useAutocompleteReviewLabResultsTask(order)

  const { data: participant } = useSearchOne(
    Participant,
    {
      _id: resolveId(order.participant),
    },
    { query: { enabled: !!order.participant } }
  )
  const [selectedDocument, setSelectedDocument] =
    useState<ExtendedDocumentReference | null>(null)
  const [isDocumentSheetOpen, setIsDocumentSheetOpen] = useState(false)

  const handleReviewComplete = useCallback(
    (documentReference: Reference<DocumentReference>) => {
      if (!selectedDocument || !documentReferences.length) {
        return
      }
      const currentIndex = documentReferences.findIndex(
        (d) => d.id === selectedDocument.id
      )
      if (currentIndex === -1) {
        setSelectedDocument(null)
        setIsDocumentSheetOpen(false)
        return
      }
      const nextIndex = currentIndex + 1
      if (nextIndex < documentReferences.length) {
        setSelectedDocument(documentReferences[nextIndex])
      } else {
        setSelectedDocument(null)
        setIsDocumentSheetOpen(false)
      }
    },
    [selectedDocument, documentReferences]
  )

  const { data: visitNote } = useSearchOne(
    VisitNote,
    (order.encounter && VisitNote.byEncounter(order.encounter)) ?? { _id: '' },
    { query: { enabled: !!order.encounter } }
  )

  const appointmentsQuery = useSearch(
    appointments.ExtendedAppointment,
    {
      'based-on': order.reference,
      status: 'booked',
    },
    { query: { enabled: !!order.reference } }
  )
  const relatedAppointments = appointmentsQuery.data ?? []
  // Standalone urgency label, rendered on its own row in each layout
  // independent of scheduling or read-only state, so it stays visible after an
  // appointment is booked — auditors reviewing CMC orders need a record of the
  // intended window even once an appointment exists.
  const appointmentUrgency = order.priority ? (
    <span className="text-xs text-muted-foreground">
      Appointment urgency · {orderPriorityTooltipLabel(order.priority)}
    </span>
  ) : null
  const specialistHealthcareServiceQuery = useSearchOne(
    healthcareServices.ExtendedHealthcareService,
    order.categoryCode === OrderCategory.Lab
      ? healthcareServices.ExtendedHealthcareService.queryLabDraws()
      : healthcareServices.ExtendedHealthcareService.querySpecialistType()
  )

  const visitNoteHref = useVisitNoteHref(visitNote)
  const navigateFromClick = useNavigateFromClickEvent()

  const sidebarContent = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-col gap-1">
          <Label className="group flex items-center gap-1">
            Requester
            <Tooltip>
              <TooltipTrigger>
                <IconInfoCircle
                  size={14}
                  className="text-muted-foreground opacity-0 group-hover:opacity-100"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="p-0 text-xs text-muted-foreground">
                  The provider who is requesting the order.
                </p>
              </TooltipContent>
            </Tooltip>
          </Label>
          <div>
            {isOfType(order.requester, 'Practitioner') && (
              <PractitionerDisplay practitioner={order.requester} />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="group flex items-center gap-1">
            Rendering Provider / Vendor
            <Tooltip>
              <TooltipTrigger>
                <IconInfoCircle
                  size={14}
                  className="text-muted-foreground opacity-0 group-hover:opacity-100"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="p-0 text-xs text-muted-foreground">
                  The provider or vendor who will be fulfilling the service or
                  supplies.
                </p>
              </TooltipContent>
            </Tooltip>
          </Label>
          <div className="">
            {isOfType(order.performer, 'Practitioner') && (
              <PractitionerDisplay practitioner={order.performer} />
            )}
            {isOfType(order.performer, 'Organization') && (
              <div className="flex items-center gap-1">
                <IconBuilding size={16} />
                {referenceDisplay(order.performer)}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label>Participant</Label>
          <ParticipantNameplate participant={order.participant} />
        </div>
        <div className="flex flex-col gap-1">
          {order.signedDate ? (
            <>
              <Label>Signed by</Label>
              <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
                {order.signedBy && (
                  <>
                    <PractitionerDisplay practitioner={order.signedBy} />
                    <DotSeparator />
                  </>
                )}
                <DateTimeDisplay value={order.signedDate} />
              </div>
            </>
          ) : (
            <OrderSignature
              order={order}
              onEditOrder={onEditOrder}
              isTwoColumn
            />
          )}
        </div>
      </div>

      <div className="border-t pt-4">
        {isReadOnly ? (
          <div className="flex flex-wrap items-center gap-2">
            <Label>Labels</Label>
            {order.labels.map((label) => (
              <OrderLabelBadge key={label} label={label} />
            ))}
          </div>
        ) : (
          <ResourceFieldForm
            instance={order}
            valueFn={(o) => o.labels}
            patchFn={(o, value) => {
              o.labels = value
            }}
            render={({ field: { value, onChange, ...rest } }) => (
              <OrderLabelCombobox value={value} onChange={onChange}>
                <div className="flex flex-wrap items-center gap-2" {...rest}>
                  <Label>Labels</Label>
                  {value.map((label) => (
                    <OrderLabelBadge key={label} label={label} />
                  ))}
                  <Button
                    variant="ghost"
                    size="unstyled"
                    className="size-6.5 rounded-full"
                  >
                    <IconPlus className="size-4 shrink-0" />
                  </Button>
                </div>
              </OrderLabelCombobox>
            )}
          />
        )}
      </div>

      {(order.supportsAuthorizationLink || order.authorizationId) &&
        participant && (
          <div className="border-t pt-4">
            <AuthorizationLink order={order} participant={participant} />
          </div>
        )}

      {documentReferences && documentReferences.length > 0 && (
        <div className="flex flex-col gap-2 border-t pt-4">
          <Label>Documents</Label>
          <div className="flex flex-wrap gap-2">
            {documentReferences.map((doc) => {
              const hasReviewTask = taskedForReview.has(
                getReferenceString(doc.reference)
              )
              return (
                <Tooltip key={doc.id}>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      color={hasReviewTask ? 'blue' : undefined}
                      className="flex max-w-sm cursor-pointer items-center gap-1"
                      onClick={() => {
                        setSelectedDocument(doc)
                        setIsDocumentSheetOpen(true)
                      }}
                    >
                      <IconFile size={16} className="shrink-0" />
                      <span className="line-clamp-1 truncate break-all text-ellipsis">
                        {doc.displayTitle}
                      </span>
                    </Badge>
                  </TooltipTrigger>
                  {hasReviewTask && (
                    <TooltipContent>Tasked for review</TooltipContent>
                  )}
                </Tooltip>
              )
            })}
          </div>
        </div>
      )}

      {showSuggestedOrderDocuments &&
        suggestedDocuments &&
        suggestedDocuments.length > 0 && (
          <div className="flex flex-col gap-2 border-t pt-4">
            <div className="flex items-center justify-between gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label className="flex cursor-help items-center gap-1">
                    Suggested Documents
                    <IconSparkles size={14} />
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    These documents have not yet been triaged in the document
                    inbox and might be related to this order based on AI
                    analysis
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestedDocuments.map((doc) => {
                const prediction = doc.predictedOrders?.find(
                  (pred) =>
                    getReferenceString(pred.reference) ===
                    getReferenceString(order.reference)
                )
                return (
                  <Tooltip key={doc.id}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="flex max-w-sm cursor-pointer items-center gap-1 border-dashed opacity-70 hover:opacity-100"
                        onClick={() => {
                          setSelectedDocument(doc)
                          setIsDocumentSheetOpen(true)
                        }}
                      >
                        <IconFile size={16} className="shrink-0" />
                        <span className="line-clamp-1 truncate break-all text-ellipsis">
                          {doc.displayTitle}
                        </span>
                      </Badge>
                    </TooltipTrigger>
                    {prediction?.reason && (
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{prediction.reason}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Confidence:{' '}
                          {Math.round((prediction.confidence ?? 0) * 100)}%
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                )
              })}
            </div>
          </div>
        )}

      {relatedAppointments.length > 0 && (
        <div className="flex flex-col gap-2 border-t pt-4">
          <Label>Appointments</Label>
          <div className="flex flex-wrap gap-2">
            {relatedAppointments.map((apt) => (
              <AppointmentBadge key={apt.id} appointment={apt} />
            ))}
            {!isReadOnly && (
              <ScheduleAppointmentLink
                order={order}
                serviceReference={
                  specialistHealthcareServiceQuery.data?.reference
                }
                serviceLookupPending={
                  specialistHealthcareServiceQuery.isPending
                }
                iconOnly
              />
            )}
          </div>
        </div>
      )}

      {!isReadOnly && relatedAppointments.length === 0 && (
        <div className="border-t pt-4">
          <ScheduleAppointmentLink
            order={order}
            serviceReference={specialistHealthcareServiceQuery.data?.reference}
            serviceLookupPending={specialistHealthcareServiceQuery.isPending}
          />
        </div>
      )}

      {appointmentUrgency}

      <div className="border-t pt-4">
        <RelatedOrdersList order={order} condensed={isTwoColumn} />
      </div>

      {visitNote && (
        <div className="border-t pt-4">
          <Button
            variant="unstyled"
            className="w-fit p-2"
            onClick={(e) => {
              invariant(visitNoteHref, 'Invalid visit note URL')
              navigateFromClick(visitNoteHref, e)
            }}
          >
            <Badge
              className="flex max-w-sm items-center gap-1"
              variant="outline"
            >
              <IconNotes size={16} className="shrink-0" />
              {visitNote.title}
            </Badge>
          </Button>
        </div>
      )}
    </div>
  )

  const mainContent = (
    <div className="flex flex-col gap-4">
      <LabSubmissionErrorDisplay order={order} />
      <div className="flex flex-col gap-2 rounded-md bg-muted p-4 text-wrap wrap-break-word">
        {orderDetails && (
          <RichTextEditor
            defaultValue={orderDetails}
            disabled
            variant="unstyled"
          />
        )}
        {order.reasonCode
          ?.filter((c) => !c.coding?.length)
          ?.flatMap((c) => c.text)
          ?.filter((v) => !!v)
          ?.map((v) => (
            <p key={v} className="text-sm">
              {v}
            </p>
          ))}
        {getCodingsFromCodeableConcept(order.reasonCode)?.map((coding) => (
          <p key={coding.code} className="text-sm">
            {uniqBy(compact([coding.code, coding.display]), (v) =>
              v.toLowerCase()
            ).join(' - ')}
          </p>
        ))}
        {order.dueDate && (
          <p className="text-sm">
            <strong>Coordination Due Date:</strong> {formatDate(order.dueDate)}
          </p>
        )}
        {order.collectionDate && (
          <p className="text-sm">
            <strong>Suggested Collection Date:</strong>{' '}
            {formatDate(order.collectionDate)}
          </p>
        )}
        {order.patientInstructions && (
          <p className="text-sm">
            <strong>Patient Instructions:</strong> {order.patientInstructions}
          </p>
        )}
        {order.displayCodes.map((coding) => (
          <p key={coding.code} className="text-sm">
            {uniqBy(compact([coding.code, coding.display]), (v) =>
              v.toLowerCase()
            ).join(' - ')}
          </p>
        ))}
      </div>

      <OrderObservationsTable order={order} />
    </div>
  )

  if (isTwoColumn) {
    return (
      <div className="flex min-w-0 gap-4">
        <div className="space-y-6 min-w-0 flex-1">
          {mainContent}
          <OrderTasks order={order} />
          <Activity
            className="px-2"
            resource={order}
            subject={order.participant}
          >
            <OrderActivity order={order} />
          </Activity>
        </div>
        <div className="w-72 shrink-0 overflow-hidden border-l pl-4">
          {sidebarContent}
        </div>
        <DocumentReferenceSheet
          documentReference={selectedDocument}
          open={isDocumentSheetOpen}
          setOpen={setIsDocumentSheetOpen}
          hideDelete
          onReviewComplete={handleReviewComplete}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex flex-col gap-1">
          <Label className="group flex items-center gap-1">
            Requester
            <Tooltip>
              <TooltipTrigger>
                <IconInfoCircle
                  size={14}
                  className="text-muted-foreground opacity-0 group-hover:opacity-100"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="p-0 text-xs text-muted-foreground">
                  The provider who is requesting the order.
                </p>
              </TooltipContent>
            </Tooltip>
          </Label>
          <div>
            {isOfType(order.requester, 'Practitioner') && (
              <PractitionerDisplay practitioner={order.requester} />
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="group flex items-center gap-1">
            Rendering Provider / Vendor
            <Tooltip>
              <TooltipTrigger>
                <IconInfoCircle
                  size={14}
                  className="text-muted-foreground opacity-0 group-hover:opacity-100"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p className="p-0 text-xs text-muted-foreground">
                  The provider or vendor who will be fulfilling the service or
                  supplies.
                </p>
              </TooltipContent>
            </Tooltip>
          </Label>
          <div className="">
            {isOfType(order.performer, 'Practitioner') && (
              <PractitionerDisplay practitioner={order.performer} />
            )}
            {isOfType(order.performer, 'Organization') && (
              <div className="flex items-center gap-1">
                <IconBuilding size={16} />
                {referenceDisplay(order.performer)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex flex-col gap-1">
          <Label>Participant</Label>
          <ParticipantNameplate participant={order.participant} />
        </div>
        <div className="flex flex-col gap-1">
          {order.signedDate ? (
            <>
              <Label>Signed by</Label>
              <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
                {order.signedBy && (
                  <>
                    <PractitionerDisplay practitioner={order.signedBy} />
                    <DotSeparator />
                  </>
                )}
                <DateTimeDisplay value={order.signedDate} />
              </div>
            </>
          ) : (
            <OrderSignature order={order} onEditOrder={onEditOrder} />
          )}
        </div>
      </div>
      {mainContent}
      {(order.supportsAuthorizationLink || order.authorizationId) &&
        participant && (
          <AuthorizationLink order={order} participant={participant} />
        )}
      {isReadOnly ? (
        <div className="flex flex-wrap items-center gap-2">
          <Label>Labels</Label>
          {order.labels.map((label) => (
            <OrderLabelBadge key={label} label={label} />
          ))}
        </div>
      ) : (
        <ResourceFieldForm
          instance={order}
          valueFn={(o) => o.labels}
          patchFn={(o, value) => {
            o.labels = value
          }}
          render={({ field: { value, onChange, ...rest } }) => (
            <OrderLabelCombobox value={value} onChange={onChange}>
              <div className="flex flex-wrap items-center gap-2" {...rest}>
                <Label>Labels</Label>
                {value.map((label) => (
                  <OrderLabelBadge key={label} label={label} />
                ))}
                <Button
                  variant="ghost"
                  size="unstyled"
                  className="size-6.5 rounded-full"
                >
                  <IconPlus className="size-4 shrink-0" />
                </Button>
              </div>
            </OrderLabelCombobox>
          )}
        />
      )}
      {documentReferences && documentReferences.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label>Documents</Label>
          <div className="flex flex-wrap gap-2">
            {documentReferences.map((doc) => {
              const hasReviewTask = taskedForReview.has(
                getReferenceString(doc.reference)
              )
              return (
                <Tooltip key={doc.id}>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="outline"
                      color={hasReviewTask ? 'blue' : undefined}
                      className="flex max-w-sm cursor-pointer items-center gap-1"
                      onClick={() => {
                        setSelectedDocument(doc)
                        setIsDocumentSheetOpen(true)
                      }}
                    >
                      <IconFile size={16} className="shrink-0" />
                      <span className="line-clamp-1 truncate break-all text-ellipsis">
                        {doc.displayTitle}
                      </span>
                    </Badge>
                  </TooltipTrigger>
                  {hasReviewTask && (
                    <TooltipContent>Tasked for review</TooltipContent>
                  )}
                </Tooltip>
              )
            })}
          </div>
        </div>
      )}
      {showSuggestedOrderDocuments &&
        suggestedDocuments &&
        suggestedDocuments.length > 0 && (
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label className="flex cursor-help items-center gap-1">
                    Suggested Documents
                    <IconSparkles size={14} />
                  </Label>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-xs">
                    These documents have not yet been triaged in the document
                    inbox and might be related to this order based on AI
                    analysis
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestedDocuments.map((doc) => {
                const prediction = doc.predictedOrders?.find(
                  (pred) =>
                    getReferenceString(pred.reference) ===
                    getReferenceString(order.reference)
                )
                return (
                  <Tooltip key={doc.id}>
                    <TooltipTrigger asChild>
                      <Badge
                        variant="outline"
                        className="flex max-w-sm cursor-pointer items-center gap-1 border-dashed opacity-70 hover:opacity-100"
                        onClick={() => {
                          setSelectedDocument(doc)
                          setIsDocumentSheetOpen(true)
                        }}
                      >
                        <IconFile size={16} className="shrink-0" />
                        <span className="line-clamp-1 truncate break-all text-ellipsis">
                          {doc.displayTitle}
                        </span>
                      </Badge>
                    </TooltipTrigger>
                    {prediction?.reason && (
                      <TooltipContent>
                        <p className="max-w-xs text-xs">{prediction.reason}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Confidence:{' '}
                          {Math.round((prediction.confidence ?? 0) * 100)}%
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                )
              })}
            </div>
          </div>
        )}
      {relatedAppointments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {relatedAppointments.map((apt) => (
            <AppointmentBadge key={apt.id} appointment={apt} />
          ))}
          {!isReadOnly && (
            <ScheduleAppointmentLink
              order={order}
              serviceReference={
                specialistHealthcareServiceQuery.data?.reference
              }
              serviceLookupPending={specialistHealthcareServiceQuery.isPending}
              iconOnly
            />
          )}
        </div>
      )}
      {!isReadOnly && relatedAppointments.length === 0 && (
        <div className="flex items-center gap-3">
          <ScheduleAppointmentLink
            order={order}
            serviceReference={specialistHealthcareServiceQuery.data?.reference}
            serviceLookupPending={specialistHealthcareServiceQuery.isPending}
          />
        </div>
      )}

      {appointmentUrgency}

      <OrderTasks order={order} />

      <RelatedOrdersList order={order} />

      <Activity className="px-2" resource={order} subject={order.participant}>
        <OrderActivity order={order} />
      </Activity>
      <DocumentReferenceSheet
        documentReference={selectedDocument}
        open={isDocumentSheetOpen}
        setOpen={setIsDocumentSheetOpen}
        hideDelete
        onReviewComplete={handleReviewComplete}
      />
    </div>
  )
}

const EMPTY_OBSERVATIONS: ExtendedObservation[] = []

interface DiagnosticReportWithObservations {
  diagnosticReport: ExtendedDiagnosticReport
  observations: ExtendedObservation[]
}

function useDiagnosticReportsForOrder(
  order: Order
): DiagnosticReportWithObservations[] {
  // Fetch all DiagnosticReports for this order
  const { data: diagnosticReports } = useSearch(
    ExtendedDiagnosticReport,
    ExtendedDiagnosticReport.byBasedOn(order.reference)
  )

  // Fetch all top-level observations linked to DiagnosticReports (these may have hasMember)
  const { data: topLevelObservations } = useSearch(ExtendedObservation, {
    '_has:DiagnosticReport:result:based-on': order.reference,
  })

  // Extract all leaf observation IDs from hasMember relationships
  const leafObservationIds = compact(
    topLevelObservations
      ?.flatMap((o) =>
        o.hasMember?.length
          ? o.hasMember?.filter((m) => isOfType(m, 'Observation'))
          : [o?.reference]
      )
      ?.map(resolveId)
  )

  // Fetch all leaf observations
  const { data: leafObservations } = useSearch(ExtendedObservation, {
    _id: leafObservationIds,
  })

  const finalObservations = (leafObservations ?? EMPTY_OBSERVATIONS).filter(
    (o) =>
      !o.derivedFrom && !o.hasProfile(HEALTH_GORILLA_OBSERVATION_GROUP_PROFILE)
  )

  // Group observations by their DiagnosticReport
  return useMemo(() => {
    if (!diagnosticReports?.length) {
      return []
    }

    return diagnosticReports.map((report) => {
      // Get top-level observations for this report
      const reportTopLevelObs = topLevelObservations?.filter((obs) =>
        report.resultIds.includes(obs.id ?? '')
      )

      // Get all leaf observation IDs from these top-level observations
      const reportLeafObsIds = new Set(
        compact(
          reportTopLevelObs?.flatMap((o) =>
            o.hasMember?.length
              ? o.hasMember
                  ?.filter((m) => isOfType(m, 'Observation'))
                  .map(resolveId)
              : [o.id]
          )
        )
      )

      // Filter final observations to those belonging to this report
      const reportObservations = finalObservations.filter((obs) =>
        reportLeafObsIds.has(obs.id ?? '')
      )

      return {
        diagnosticReport: report,
        observations: reportObservations,
      }
    })
  }, [diagnosticReports, topLevelObservations, finalObservations])
}

function OrderObservationsTable({ order }: { order: Order }): React.ReactNode {
  const diagnosticReports = useDiagnosticReportsForOrder(order)

  if (diagnosticReports.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      {diagnosticReports.map(({ diagnosticReport, observations }) => {
        const [messages, regularObservations] = partition(
          observations,
          (o) => o.isMessageType
        )
        const [normalObs, abnormalObs] = partition(
          regularObservations,
          (o) => o.isNormal
        )
        const sortedObservations = [
          ...sortBy(abnormalObs, (o) => o.display),
          ...sortBy(normalObs, (o) => o.display),
        ]

        const presentedFormURL = diagnosticReport.presentedForm?.[0]?.url

        return (
          <Card key={diagnosticReport.id} className="p-0">
            <div className="border-b bg-muted/50 p-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-1">
                  <ExternalTextLink href={presentedFormURL ?? '#'}>
                    <h4 className="font-medium">
                      {diagnosticReport.display ?? 'Diagnostic Report'}
                    </h4>
                  </ExternalTextLink>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {diagnosticReport.issued && (
                      <DateTimeDisplay value={diagnosticReport.issued} />
                    )}
                    {diagnosticReport.status !== 'final' && (
                      <Badge variant="outline" className="text-xs">
                        {diagnosticReport.status}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              {diagnosticReport.conclusion && (
                <p className="mt-2 text-sm text-muted-foreground">
                  {diagnosticReport.conclusion}
                </p>
              )}
            </div>

            {messages.length > 0 && (
              <div className="flex flex-col gap-1 p-2">
                {messages.map((m) => (
                  <p key={m.id} className="text-sm">
                    <span className="font-bold">{m.display}</span>{' '}
                    {m.noteFirstText} {m.valueString}
                  </p>
                ))}
              </div>
            )}

            {sortedObservations.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Value</TableHead>
                    <TableHead>Reference Range</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedObservations.map((o) => (
                    <HoverCard key={o.id} openDelay={100} closeDelay={100}>
                      <HoverCardTrigger asChild>
                        <TableRow
                          className={cn(
                            !o.isNormal && 'font-bold text-red-500'
                          )}
                        >
                          <TableCell className="py-2">{o.display}</TableCell>
                          <TableCell className="py-2">
                            <p className="flex items-center gap-1">
                              {o.valueDisplay}
                              {o.status !== 'final' && (
                                <Badge variant="outline">{o.status}</Badge>
                              )}
                            </p>
                            {o.noteFirstText && (
                              <p className="text-sm font-normal text-muted-foreground italic">
                                {o.noteFirstText}
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="py-2">
                            {o.referenceRange?.[0].text ??
                              o.interpretationDisplay}
                          </TableCell>
                        </TableRow>
                      </HoverCardTrigger>
                      {/* The chart's tooltip floats above the chart, outside
                          the card. A scrolling card cuts it off. */}
                      <HoverCardContent className="w-80 overflow-visible p-2">
                        <ObservationHistoryChart
                          observation={o}
                          subject={order.participant}
                          coding={o.codeableConcept}
                        />
                      </HoverCardContent>
                    </HoverCard>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        )
      })}
    </div>
  )
}

const useAutocompleteReviewLabResultsTask = (order: Order): void => {
  const practitioner = useCurrentPractitioner()
  const { data: task } = useSearchOne(
    ReviewLabResultsTask,
    ReviewLabResultsTask.byFocus(order.reference)
  )
  const { mutate: patchTask, isPending } = usePatchMutation<StandardTask>()
  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  useEffect(() => {
    if (order.category !== OrderCategory.Lab) {
      return
    }
    if (!task || task.state === 'done') {
      return
    }
    if (
      !task.ownerPractitioner ||
      getReferenceString(task.ownerPractitioner) !==
        practitioner.reference.reference
    ) {
      return
    }
    if (isPending) {
      return
    }
    patchTask(
      {
        instance: task,
        mutateFn: (t) => {
          t.state = 'done'
        },
      },
      {
        onSuccess: () => {
          markRelatedInboxItemsAsDone(
            createTaskFilter([
              {
                taskReferenceString: task.reference.reference,
                state: 'done',
              },
            ])
          )
        },
      }
    )
  }, [
    patchTask,
    order,
    task,
    isPending,
    practitioner,
    markRelatedInboxItemsAsDone,
  ])
}

function AuthorizationLink({
  order,
  participant,
}: {
  order: Order
  participant: Participant
}): React.ReactNode {
  const isReadOnly = useIsReadOnly()
  const patch = usePatchMutation<Order>()
  const { seenApi } = useSeenApi()
  const { handleSearch } = useAstranaAuthorizationSearch(
    participant,
    order.authorizationId
  )
  const [isLinking, setIsLinking] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [bypassError, setBypassError] = useState(false)
  const [authorizationIdInput, setAuthorizationIdInput] = useState('')
  const authorizationIdInputChanged = useDidChange(authorizationIdInput)
  if (authorizationIdInputChanged) {
    setBypassError(false)
  }

  const { open: openDeleteConfirmation } = useDeleteConfirmationStore()

  const authorizationQuery = useQuery({
    queryKey: ['authorization', authorizationIdInput],
    queryFn: async () => {
      const response = await seenApi.post('/astrana/get-auth-details', {
        authorizationId: authorizationIdInput,
      })
      return response.data
    },
    enabled: !!authorizationIdInput.trim() || isEditing,
    retry: false,
  })

  const handleFetchDetails = (): void => {
    if (authorizationIdInput.trim()) {
      void authorizationQuery.refetch()
    }
  }

  const handleConfirm = async (): Promise<void> => {
    if (!authorizationIdInput.trim()) {
      return
    }

    if (!authorizationQuery.data && !bypassError) {
      return
    }

    try {
      await patch.mutateAsync({
        instance: order,
        mutateFn: (o: Order) => {
          o.authorizationId = authorizationIdInput.trim()
        },
      })
      setIsLinking(false)
      setIsEditing(false)
      setAuthorizationIdInput('')
      setBypassError(false)
      if (isEditing) {
        toast.success('Authorization updated successfully')
      } else {
        toast.success('Authorization linked successfully')
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      console.error(`Failed to update order with authorization ID: ${message}`)
      if (isEditing) {
        toast.error(`Failed to update authorization ${message}`)
      } else {
        toast.error(`Failed to link authorization ${message}`)
      }
    }
  }

  const handleCancel = (): void => {
    setIsLinking(false)
    setIsEditing(false)
    setAuthorizationIdInput('')
    setBypassError(false)
  }

  const handleDelete = (): void => {
    openDeleteConfirmation({
      title: 'Unlink Authorization',
      description: `Are you sure you want to unlink the authorization "${order.authorizationId}" from this order?`,
      deleteText: 'Unlink',
      onConfirm: () => {
        void (async () => {
          try {
            await patch.mutateAsync({
              instance: order,
              mutateFn: (o: Order) => {
                o.authorizationId = undefined
              },
            })
            toast.success('Authorization link removed')
          } catch (error) {
            const message =
              error instanceof Error ? error.message : 'Unknown error'
            console.error(`Failed to remove authorization link: ${message}`)
            toast.error(`Failed to remove authorization link ${message}`)
          }
        })()
      },
    })
  }

  if (order.authorizationId && !isEditing) {
    return (
      <div className="group flex flex-wrap items-center gap-1 text-sm">
        <Label>Authorization ID</Label>
        <button
          type="button"
          className="flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 underline"
          onClick={handleSearch}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              handleSearch()
            }
          }}
        >
          {order.authorizationId}
          <IconExternalLink size={14} className="text-muted-foreground" />
        </button>
        {!isReadOnly && (
          <div className="ml-1 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setIsEditing(true)
                setAuthorizationIdInput(order.authorizationId ?? '')
              }}
              className="h-6 w-6 p-0"
            >
              <IconEdit size={14} className="text-muted-foreground" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="h-6 w-6 p-0 text-destructive hover:text-destructive"
            >
              <IconUnlink size={14} />
            </Button>
          </div>
        )}
      </div>
    )
  }

  if (isLinking || isEditing) {
    const errors: string[] = []
    if (
      authorizationQuery.data &&
      authorizationQuery.data.participantId !== participant.seenMRN
    ) {
      errors.push(
        `MRN mismatch - ${participant.seenMRN} (${participant.name}) from order does not match ${authorizationQuery.data.participantId} (${authorizationQuery.data.participantName}) from authorization`
      )
    }

    return (
      <div className="space-y-4 rounded-md border p-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">
            {isEditing ? 'Edit Authorization' : 'Link Authorization'}
          </h4>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancel}
            disabled={patch.isPending}
          >
            <IconX size={14} className="text-muted-foreground" />
          </Button>
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="authorization-id">Authorization ID</Label>
            <div className="flex gap-2">
              <Input
                id="authorization-id"
                value={authorizationIdInput}
                autoFocus
                onChange={(e) => setAuthorizationIdInput(e.target.value)}
                variant="underline"
                placeholder="Enter authorization ID"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleFetchDetails()
                  }
                }}
              />
              <Button
                onClick={handleFetchDetails}
                disabled={!authorizationIdInput.trim()}
                loading={authorizationQuery.isFetching}
                variant="outline"
                size="sm"
              >
                Get Details
              </Button>
            </div>
          </div>

          {authorizationQuery.isError && (
            <div className="space-y-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <div>
                Failed to fetch authorization details.{' '}
                {authorizationQuery.error.message}
              </div>
              <div className="space-x-2 flex items-center">
                <Checkbox
                  id="bypass-error-fetch"
                  value={bypassError}
                  onChange={(checked) => setBypassError(!!checked)}
                />
                <Label
                  htmlFor="bypass-error-fetch"
                  className="text-sm font-normal"
                >
                  Error bypass: I have double-checked the authorization details
                  in the external system and confirmed they match the order
                </Label>
              </div>
            </div>
          )}

          {authorizationQuery.data && (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <h5 className="text-sm font-medium">Authorization Details</h5>
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-1">
                  <span className="font-medium">
                    {authorizationQuery.data.referrerName}
                  </span>
                  <span>referring</span>
                  <span className="font-medium">
                    {authorizationQuery.data.participantName} (
                    {authorizationQuery.data.participantId})
                  </span>
                  <span>to</span>
                  <span className="font-medium">
                    {authorizationQuery.data.referreeName}
                  </span>
                  {authorizationQuery.data.referreeSpecialty && (
                    <>
                      <span>for</span>
                      <span className="font-medium">
                        {authorizationQuery.data.referreeSpecialty}
                      </span>
                    </>
                  )}
                  {authorizationQuery.data.diagnosis.length > 0 && (
                    <>
                      <span>based on</span>
                      <span>
                        {authorizationQuery.data.diagnosis
                          .map((d: { display: string }) => d.display)
                          .join(', ')}
                      </span>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                  <span>Auth ID: {authorizationQuery.data.id}</span>
                  <span>•</span>
                  <span>Requested: {authorizationQuery.data.requestDate}</span>
                </div>
              </div>
            </div>
          )}

          {errors.length > 0 && (
            <div className="space-y-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              <div>{errors.join(', ')}</div>
              <div className="space-x-2 flex items-center">
                <Checkbox
                  id="bypass-error"
                  value={bypassError}
                  onChange={(checked) => setBypassError(!!checked)}
                />
                <Label htmlFor="bypass-error" className="text-sm font-normal">
                  Error bypass: I have double-checked the authorization details
                  in the external system and confirmed they match the order
                </Label>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={patch.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={
                !authorizationIdInput.trim() ||
                (!authorizationQuery.data && !bypassError) ||
                patch.isPending ||
                (errors.length > 0 && !bypassError)
              }
              loading={patch.isPending}
            >
              {isEditing ? 'Update' : 'Link'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (isReadOnly) {
    return null
  }

  return (
    <Button
      variant="outline"
      size="sm"
      className="flex w-fit items-center gap-1"
      onClick={() => setIsLinking(true)}
    >
      <IconLink size={14} className="text-muted-foreground" />
      Link Authorization
    </Button>
  )
}

export interface ScheduleAppointmentLinkProps {
  order: Order
  serviceReference: Reference<HealthcareService> | undefined
  // Whether the service lookup is still in flight. The link is disabled only
  // while pending — a lookup that settles without a match (org without the
  // specialist catalog entry, or an error) falls back to a link without the
  // visit type, so scheduling is never stranded behind a disabled button.
  serviceLookupPending: boolean
  iconOnly?: boolean
}

// Deep link into the calendar's appointment request panel, carrying the
// order's specialty and performer so the form prefills synchronously instead
// of re-deriving them from a patient-scoped orders search. Disabled while the
// healthcare service lookup is in flight: a link built without
// requestHealthcareService opens a form that can't identify the visit type,
// so the specialty prefill silently never applies. Exported for its
// URL-contract tests and stories.
export function ScheduleAppointmentLink({
  order,
  serviceReference,
  serviceLookupPending,
  iconOnly = false,
}: ScheduleAppointmentLinkProps): JSX.Element {
  const pending = !serviceReference && serviceLookupPending
  const button = iconOnly ? (
    <Button variant="ghost" size="icon" disabled={pending}>
      <IconCalendarPlus size={14} />
    </Button>
  ) : (
    <Button variant="outline" size="sm" disabled={pending}>
      <IconCalendarPlus size={14} className="mr-2" /> Schedule Appointment
    </Button>
  )

  if (pending) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {/* oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- disabled button swallows pointer/focus events so wrapper span must be focusable for Radix tooltip to open */}
          <span tabIndex={0} className="w-fit">
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent>Loading visit type…</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Link
      to={`/calendar?${buildCalendarQueryParam({
        viewType: ViewType.TimeGrid,
        viewHorizon: ViewHorizon.Week,
        requestRequiresTransportation: false,
        showRequestAppointment: true,
        appointmentStatuses: ['booked'],
        // The service is request state only, never a calendar filter: the
        // query ORs filter types, and the shared clinical-specialist service
        // would flood the grid with every specialist appointment.
        appointmentFilters: compact([order.participant]),
        requestPatient: order.participant,
        requestHealthcareService: serviceReference,
        requestPractitioner: order.performerPractitioner,
        // The specialty only means something on the specialist service line,
        // so it rides along only when the service resolved.
        requestSpecialty: serviceReference ? order.specialtyValue : undefined,
        requestBasedOn: order.reference,
        aggregateCalendarFilters: [],
      })}`}
      className="w-fit"
    >
      {button}
    </Link>
  )
}
